import { useState, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useStripe, useElements, CardCvcElement } from '@stripe/react-stripe-js'
import { startOfDay } from 'date-fns'
import { Trans, useTranslation } from 'react-i18next'

import { formatPrice } from '@/utils'

import { selectUserRole } from '../../../../reducers/userDataReducer'
import { fetchSingleBuilding } from '../../../../reducers/buildingSelectorReducer'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import CreditCardContent from '../../../CreditCardContent/CreditCardContent'
import ModalContainer from '../../../ModalContainer/ModelContainer'
import DateField from '../../../FormLegacy/FormFieldsV2/DateField'
import SuccessConfirmationModal from '../../../Popups/SuccessConfirmationModal'
import Icon from '../../../Icons'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import API from '../../../../api/api'

import AddFundsToolbar from './components/AddFundsToolbar/AddFundsToolbar'
import {
  displayPayButton,
  isCurrentStepInvoice,
  isCurrentStepCard,
  calculateConvenienceFee,
} from './utils'

const CONVENIENCE_FEE_PERCENTAGE = 3.5
const EMAIL_FOR_INVOICE = 'hello@elevatedliving.com'

const RewardsAddFundsModal = ({
  onClose = () => {},
  selectedBuilding,
  userRole,
  fetchSingleBuilding,
}) => {
  const { t } = useTranslation(['rewards-gift', 'common'])
  const [isLoading, setLoading] = useState(false)
  const [isLoadingBuildingCard, setLoadingBuildingCard] = useState(false)
  const [buildingCreditCard, setBuildingCreditCard] = useState(null)
  const [cardOwnerFullName, setCardOwnerFullName] = useState(null)
  const [invoiceConfirmModalVisible, setInvoiceConfirmModalVisible] = useState(false)
  const [formState, setFormState] = useState({
    amount: null,
    currentTab: 'card',
    startDate: new Date(),
  })

  const [cardData, setCardData] = useState({
    cardNumber: false,
    cardCvc: false,
    cardExpiry: false,
  })
  const stripe = useStripe()
  const elements = useElements()
  const { displaySnackbar } = useSnackbar()

  const verifyCardDataChanges = (event) =>
    setCardData({ ...cardData, [event.elementType]: event.complete })

  useEffect(() => {
    if (selectedBuilding) {
      setLoading(false)
      setLoadingBuildingCard(true)
      API.getBuildingCreditCardData(selectedBuilding.id)
        .then(({ data }) => {
          if (data.last4) {
            setBuildingCreditCard(data)
          }
          setLoadingBuildingCard(false)
        })
        .catch((err) => {
          setLoadingBuildingCard(false)
          console.error(err)
        })
    }
  }, [selectedBuilding.id])

  const handleFieldChange = ({ target: { name, value } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleChangeAmount = (e) => {
    const max = 1000000
    const min = 0
    let value = Number(e.target.value)
    if (value > max) value = max
    if (value < min) value = min
    e.target.value = value
    return handleFieldChange(e)
  }

  const getCreditCardToken = async () => {
    const data = {
      name: cardOwnerFullName,
    }
    try {
      const cardElement = elements.getElement(CardCvcElement)
      const { token } = await stripe.createToken(cardElement, data)
      return token.id
    } catch (err) {
      displaySnackbar(err.response.data.message, true)
    }
  }

  const onSubmitFinally = async () => {
    setLoading(false)
    if (selectedBuilding.id) {
      await fetchSingleBuilding(selectedBuilding.id)
    }
  }

  const handleSubmitPayment = async () => {
    try {
      setLoading(true)
      const sendData = {
        type: formState.currentTab,
        amount: formState.amount,
      }

      if (isCurrentStepInvoice(formState.currentTab) && userRole === 'PropertyManager') {
        const date = startOfDay(formState.startDate).getTime()
        sendData.date = date
      } else if (isCurrentStepCard(formState.currentTab) && !buildingCreditCard) {
        const token = await getCreditCardToken()
        sendData.stripeToken = token
      }

      API.addFundsToBuildingBalance(selectedBuilding.id, sendData)
        .then(() => {
          if (isCurrentStepInvoice(formState.currentTab) && userRole === 'PropertyManager') {
            setInvoiceConfirmModalVisible(true)
          } else {
            displaySnackbar(t('rewards-gift:addFunds.paymentProceededSuccess'))
            onClose()
          }
        })
        .catch((err) =>
          displaySnackbar(
            err?.response?.data?.message || t('rewards-gift:addFunds.paymentProceedingError'),
            true,
          ),
        )
        .finally(onSubmitFinally)
    } catch (err) {
      displaySnackbar(err.message, true)
    }
  }

  const cardDataVerify = useMemo(
    () => cardData.cardExpiry && cardData.cardNumber && cardData.cardCvc && cardOwnerFullName,
    [cardData, cardOwnerFullName],
  )

  const isButtonDisabled = useMemo(() => {
    if (formState.currentTab === 'card') {
      return !buildingCreditCard && (!stripe || !elements || !cardDataVerify)
    }

    return isLoading || !Number(formState.amount || 0)
  }, [
    formState.currentTab,
    buildingCreditCard,
    stripe,
    elements,
    cardDataVerify,
    isLoading,
    formState.amount,
  ])

  if (invoiceConfirmModalVisible) {
    return (
      <SuccessConfirmationModal
        header={t('rewards-gift:addFunds.requestSentHeader')}
        description={
          <div>
            <Trans
              t={t}
              i18nKey="rewards-gift:addFunds.requestSentDescription"
              components={[<span key="email" />]}
            />
          </div>
        }
        handleClose={onClose}
      />
    )
  }

  return (
    <ModalContainer
      additionalClass={'rewards-modal'}
      onClose={onClose}
      headerTitle={t('rewards-gift:addFunds.paymentHeaderTitle')}
    >
      <div className="rewards-modal-content rewards-add-funds-modal">
        <div className="rewards-modal-desc balance">
          <span>{t('rewards-gift:addFunds.currentBalanceLabel')}</span>
          <span>${selectedBuilding.rewardsBalance}</span>
        </div>
        <div className="rewards-modal-input-row">
          <div className="input-container">
            <div className="input-label">{t('rewards-gift:addFunds.paymentAmountLabel')}</div>
            <TextField
              name="amount"
              maxLength="10"
              onChange={handleChangeAmount}
              value={formState.amount}
              placeholder={t('rewards-gift:sendGiftForm.enterPlaceholder')}
              style={{ width: '100%' }}
              type="number"
              leftIcon={
                <Icon
                  icon="dollar"
                  fill="none"
                  svgProps={{
                    stroke: '#3C3C3C',
                    strokeLinecap: 'round',
                    strokeLinejoin: 'round',
                  }}
                />
              }
            />
          </div>
        </div>
        <div className="rewards-modal-input-row">
          <div className="input-container">
            <div className="input-label small">
              {t('rewards-gift:addFunds.selectPaymentMethodLabel')}
            </div>
            <AddFundsToolbar
              currentTab={formState.currentTab}
              handleTab={(tab) => handleFieldChange({ target: { name: 'currentTab', value: tab } })}
            />
          </div>
        </div>
        {isCurrentStepCard(formState.currentTab) ? (
          <CreditCardContent
            isLoading={isLoading}
            isLoadingBuildingCard={isLoadingBuildingCard}
            buildingCreditCard={buildingCreditCard}
            cardOwnerFullName={cardOwnerFullName}
            verifyCardDataChanges={verifyCardDataChanges}
            setBuildingCreditCard={setBuildingCreditCard}
            setCardOwnerFullName={setCardOwnerFullName}
          />
        ) : isCurrentStepInvoice(formState.currentTab) ? (
          <div className="rewards-modal-input-row">
            <DateField
              name="startDate"
              label={t('rewards-gift:addFunds.startDateLabel')}
              placeholderText="(MM/DD/YYYY)"
              autoComplete="off"
              dateFormat="MM/dd/yyyy"
              minDate={new Date()}
              value={formState.startDate}
              onChange={(e) =>
                handleFieldChange({ target: { name: 'startDate', value: e.target.value } })
              }
              onChangeRaw={(e) => e.preventDefault()}
              disabled={isLoading}
            />
          </div>
        ) : null}
        {isCurrentStepInvoice(formState.currentTab) && userRole === 'PropertyManager' && (
          <div className="rewards-modal-desc info">
            <Icon icon="info" viewBox="0 0 25 25" />
            <span>
              <Trans
                t={t}
                i18nKey="rewards-gift:addFunds.invoiceInfo"
                components={[<a key="email" href={`mailto:${EMAIL_FOR_INVOICE}`} />]}
              />
            </span>
          </div>
        )}
        <div className="rewards-modal-summary">
          <div className="rewards-modal-summary-title">
            {t('rewards-gift:addFunds.summaryTitle')}
          </div>
          <div className="rewards-modal-summary-item">
            <div className="rewards-modal-summary-item-label">
              {t('rewards-gift:addFunds.topUpAmountLabel')}
            </div>
            <div className="rewards-modal-summary-item-value">
              {formatPrice(formState.amount * 100)}
            </div>
          </div>
          {isCurrentStepCard(formState.currentTab) && (
            <div className="rewards-modal-summary-item">
              <div className="rewards-modal-summary-item-label">
                {t('rewards-gift:addFunds.convenienceFeeLabel', {
                  percentage: CONVENIENCE_FEE_PERCENTAGE,
                })}
              </div>
              <div className="rewards-modal-summary-item-value">
                {formatPrice(
                  calculateConvenienceFee(formState.amount, CONVENIENCE_FEE_PERCENTAGE) * 100,
                )}
              </div>
            </div>
          )}
          <div className="rewards-modal-summary-item">
            <div className="rewards-modal-summary-item-label bold">
              {t('rewards-gift:addFunds.totalLabel')}
            </div>
            <div className="rewards-modal-summary-item-value bold">
              {formatPrice(
                (Number(formState.amount) +
                  (isCurrentStepCard(formState.currentTab)
                    ? Number(calculateConvenienceFee(formState.amount, CONVENIENCE_FEE_PERCENTAGE))
                    : 0)) *
                  100,
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="rewards-modal-actions">
        <button className="button button-gray" onClick={onClose} disabled={isLoading}>
          {t('common:actions.cancel')}
        </button>
        <button
          className="button button-primary auto-width"
          onClick={handleSubmitPayment}
          disabled={isButtonDisabled}
        >
          {displayPayButton(formState.currentTab, userRole, t)}
        </button>
      </div>
    </ModalContainer>
  )
}

RewardsAddFundsModal.propTypes = {
  fetchSingleBuilding: PropTypes.func,
  onClose: PropTypes.func,
  selectedBuilding: PropTypes.object,
  userRole: PropTypes.string,
}

export default connect(
  (state) => ({
    userRole: selectUserRole(state),
  }),
  { fetchSingleBuilding },
)(RewardsAddFundsModal)
